import { STATUS_COLOR_MAP } from './statusColorMap';

/** Size-specific classes for container, text, icon, and dot rendering */
export const SIZE_CLASSES = {
  small: {
    container: 'h-[20px] px-2 gap-1',
    text: 'text-[12px]',
    iconSize: 12,
    dotSize: 'xs'
  },
  medium: {
    container: 'h-[24px] px-2.5 gap-1.5',
    text: 'text-[14px]',
    iconSize: 14,
    dotSize: 'sm'
  },
  large: {
    container: 'h-[28px] px-2.5 gap-1.5',
    text: 'text-[14px]',
    iconSize: 16,
    dotSize: 'md'
  }
};

/** Tailwind container per color token */
const COLOR_CLASSES = {
  success: {
    container: 'bg-success-100'
  },
  danger: {
    container: 'bg-danger-100'
  },
  warning: {
    container: 'bg-warning-100'
  },
  primary: {
    container: 'bg-primary-100'
  },
  purple: {
    container: 'bg-purple-100'
  },
  neutral: {
    container: 'bg-neutral-100'
  }
};

/**
 * Icon name used in the `icon` indicator for each color token.
 * Uses Icon component icon names.
 */
export const ICON_PER_COLOR = {
  primary: 'info-circle-mono',
  purple: 'status-mono',
  success: 'tick-circle-mono',
  warning: 'warning-2-mono',
  danger: 'danger-mono',
  neutral: 'archive-mono'
};

/**
 * Resolves a color token from a raw status string.
 * Normalizes spaces → hyphens and lowercases before lookup.
 * Falls back to `'neutral'` for unknown statuses.
 *
 * @param {string} [status]
 * @returns {string}
 */
export const getColorFromStatus = (status) => {
  if (!status) return 'neutral';
  const normalized = status.replaceAll(' ', '_').replaceAll('-', '_').toLowerCase();
  // Practice roles are dynamic, so `arrived_with_<role>` can't be enumerated in the map.
  if (normalized.startsWith('arrived_with_')) return STATUS_COLOR_MAP.arrived_waiting;
  return STATUS_COLOR_MAP[normalized] || STATUS_COLOR_MAP[status] || 'neutral';
};

/**
 * Returns Tailwind container classes for the given color token and visual variant.
 *
 * @param {string} [color]
 * @param {'small'|'medium'|'large'} [size]
 * @param {'filled'|'outlined'|'transparent'} [variant]
 * @returns {string}
 */
export const getContainerClasses = (color, size = 'medium', variant = 'filled') => {
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.medium;
  const base = `inline-flex items-center justify-center overflow-hidden rounded-full leading-[18px] ${sizeClasses.container}`;
  if (variant === 'filled')
    return `${base} border border-solid border-black !border-opacity-[12%] bg-${color}-100`;
  if (variant === 'outlined') return `${base} bg-white border border-solid border-neutral-200`;
  if (variant === 'transparent') return base;

  // filled (default)
  const colorClasses = COLOR_CLASSES[color] || COLOR_CLASSES.neutral;
  return `${base} ${colorClasses.container}`;
};

/**
 * Returns Tailwind text classes for the given color token.
 *
 * @param {string} [color]
 * @returns {string}
 */
export const getTextClasses = (color, size = 'medium') => {
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.medium;
  return `font-500 text-neutral-900 text-ellipsis overflow-hidden first-letter:!capitalize !lowercase ${sizeClasses.text}`;
};
